'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useAuth } from '@/hooks/useAuth'
import { createClientComponentClient } from '@/integrations/supabase/client'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { Eye, EyeOff, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SocialLoginSection } from '@/components/auth/SocialLoginSection'
import GracefulErrorHandler from '@/components/auth/GracefulErrorHandler'
import { useFacilityBranding } from '@/contexts/SubdomainBrandingContext'
import BrandLogo from '@/components/common/BrandLogo'
import LanguagePicker from '@/components/university/ui/LanguagePicker'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isFacebookLoading, setIsFacebookLoading] = useState(false)
  const [error, setError] = useState('')
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [resetMessage, setResetMessage] = useState('')

  const { signIn, signInWithGoogle, signInWithFacebook, user, isLoading, resetPassword, signUp } = useAuth()
  const facilityBranding = useFacilityBranding()
  const supabase = createClientComponentClient()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [resendStatus, setResendStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  // Track submitting state in a ref to avoid changing effect deps size
  const submittingRef = useRef(isSubmitting)
  useEffect(() => { submittingRef.current = isSubmitting }, [isSubmitting])

  // ── Return-path producer for EMAIL auth (OAuth producers live in
  // AuthContext.signInWithGoogle/Facebook; the guard consumes this key).
  // Without this, an email sign-in from any deep link (e.g. /homeschool/enrol)
  // loses its origin and the guard falls through to cold-signup routing.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const redirectParam = params.get('redirect') || params.get('returnPath')
    const raw = redirectParam
      ? decodeURIComponent(redirectParam)
      : (document.referrer && new URL(document.referrer).pathname.startsWith('/') ? null : null)
    // Prefer an explicit redirect param; otherwise carry where the user
    // actually was (history state set by SPA navigation keeps full path)
    // Never store an auth page as the return path — that's the loop bug:
    // landing on /signin stored "/signin", the guard then bounced a signed-in
    // user back to /signin forever. Return paths must be PRE-auth destinations.
    const currentPath = window.history.state?.usr?.pathname ?? window.location.pathname + window.location.search
    const target = raw || currentPath
    const isAuthPage = target.startsWith('/auth') || target === '/signin' || target === '/signup' || target.startsWith('//')
    const safe = target.startsWith('/') && target !== '/' && !isAuthPage
    if (safe) {
      try { localStorage.setItem('sage_auth_return_path', target) } catch { /* non-fatal */ }
    }
  }, [])

  // If already authenticated, let AuthRedirectGuard handle routing
  useEffect(() => {
    if (!user || isLoading) return
    if (submittingRef.current) setIsSubmitting(false)
    // AuthRedirectGuard in App.tsx handles all post-auth routing
    navigate('/', { replace: true })
  }, [user, isLoading, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError('')

    try {
      const { error } = await signIn(email, password)
      if (error) {
        setError((error as any)?.message || 'Login failed')
      }
      // On success: do NOT navigate here. The useEffect below routes to '/',
      // and AuthRedirectGuard (App.tsx) performs all role-based routing:
      // parent → /homeschool/parent · child → /learn · solo → /learn ·
      // companion user → /category/friend-companion or /dashboard.
    } catch {
      setError(t('auth.login.unexpectedError'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true)
    setError('')

    try {
      const { error } = await signInWithGoogle()
      if (error) {
        setError((error as any)?.message || 'Google sign-in failed')
      }
    } catch {
      setError('Failed to sign in with Google')
    } finally {
      setIsGoogleLoading(false)
    }
  }

  const handleFacebookSignIn = async () => {
    setIsFacebookLoading(true)
    setError('')

    try {
      const { error } = await signInWithFacebook()
      if (error) {
        setError((error as any)?.message || 'Facebook sign-in failed')
      }
    } catch {
      setError('Failed to sign in with Facebook')
    } finally {
      setIsFacebookLoading(false)
    }
  }

  const handleNavigateToSignup = (emailToPrefill: string) => {
    // Navigate to signup with email pre-filled AND origin carried —
    // the return path must survive the login→signup hop
    const returnPath = (() => { try { return localStorage.getItem('sage_auth_return_path') } catch { return null } })()
    const suffix = returnPath ? `&redirect=${encodeURIComponent(returnPath)}` : ''
    navigate(`/auth/signup?email=${encodeURIComponent(emailToPrefill)}${suffix}`)
  }

  const handleDismissError = () => {
    setError('')
  }

  const handleResendVerification = async () => {
    if (!email) {
      setError('Please enter your email to resend verification.')
      return
    }
    setResendStatus('loading')
    try {
      // In Supabase, resending is done via signUp with the same email/password
      // or using the resend OTP API. The easiest is using a dedicated resend function if available,
      // but here we'll use the supabase.auth.resend method.
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/verify`,
        }
      })

      if (error) {
        setResendStatus('error')
        setError(error.message)
      } else {
        setResendStatus('success')
        setTimeout(() => setResendStatus('idle'), 5000)
      }
    } catch (err) {
      setResendStatus('error')
      setError('Failed to resend verification email.')
    }
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsResettingPassword(true)
    setResetMessage('')

    const cleanEmail = forgotPasswordEmail.trim().toLowerCase()
    if (cleanEmail.endsWith('@stillwaters.internal')) {
      setResetMessage('Student accounts — ask your parent to reset your password from their dashboard.')
      setIsResettingPassword(false)
      return
    }

    try {
      const { error } = await resetPassword(forgotPasswordEmail)

      if (error) {
        setResetMessage(`Error: ${(error as any)?.message || 'Reset failed'}`)
      } else {
        setResetMessage('Password reset email sent! Please check your inbox and spam folder.')
        setForgotPasswordEmail('')
        setTimeout(() => {
          setShowForgotPassword(false)
          setResetMessage('')
        }, 3000)
      }

    } catch {
      setResetMessage('An unexpected error occurred. Please try again.')
    } finally {
      setIsResettingPassword(false)
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0
    }
  }

  const accent = facilityBranding.is_branded ? (facilityBranding.secondary_color || '#d4a843') : '#4a7c5e'

  return (
    <div style={{ minHeight: '100dvh', background: '#020617', fontFamily: "'Inter', system-ui, sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 16px', position: 'relative', overflow: 'hidden' }}>
      {/* Radial glow — org-colored when branded, sage when SWA */}
      <div aria-hidden style={{ position: 'absolute', inset: 0, opacity: 0.12, background: `radial-gradient(ellipse 55% 40% at 50% 0%, ${accent} 0%, transparent 65%)`, pointerEvents: 'none' }} />
      <div aria-hidden style={{ position: 'absolute', inset: 0, opacity: 0.05, backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)', backgroundSize: '28px 28px', pointerEvents: 'none' }} />

      {facilityBranding.is_branded && (
        <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 50 }}>
          <LanguagePicker />
        </div>
      )}

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 540 }}
      >
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <motion.div
            variants={itemVariants}
            style={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}
          >
            {facilityBranding.is_branded && facilityBranding.logo_url ? (
              <BrandLogo src={facilityBranding.logo_url} alt={facilityBranding.display_name} style={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain' }} />
            ) : (
              <img src="/icons/blue-sage-logo.png" alt="Sage Logo" style={{ width: 80, height: 80, objectFit: 'contain' }} />
            )}
          </motion.div>

          <motion.h2 variants={itemVariants} style={{ fontSize: 'clamp(1.8rem, 5vw, 2.6rem)', fontWeight: 900, color: '#ffffff', margin: '0 0 10px', letterSpacing: '-0.02em', fontFamily: "'Playfair Display', Georgia, serif" }}>
            {facilityBranding.is_branded ? facilityBranding.display_name : t('auth.login.title')}
          </motion.h2>
          <motion.p variants={itemVariants} style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>
            {facilityBranding.is_branded ? 'Sign in to your portal' : t('auth.login.subtitle')}
          </motion.p>
        </div>

        <motion.div
          variants={itemVariants}
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 24, padding: '40px 36px', backdropFilter: 'blur(16px)', boxShadow: '0 30px 80px rgba(0,0,0,0.6)' }}
        >
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* User not found handler */}
            {error && (
              <GracefulErrorHandler
                error={error}
                email={email}
                onNavigateToSignup={handleNavigateToSignup}
                onDismiss={handleDismissError}
              />
            )}

            {/* Standard error display for ALL other errors, especially invalid credentials */}
            {error && !error.toLowerCase().includes('user not found') &&
              !error.toLowerCase().includes('not registered') &&
              !error.toLowerCase().includes('does not exist') && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="p-5 rounded-2xl font-bold mb-6"
                  style={{ background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.28)', color: '#fca5a5' }}
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <span className="text-red-300 font-bold">Error</span>
                  </div>
                  <p className="mb-3 text-sm text-red-200" style={{ fontWeight: 500 }}>
                    {error.toLowerCase().includes('invalid login credentials') || error.toLowerCase().includes('invalid email or password')
                      ? "Incorrect email or password. Please double check and try again."
                      : error}
                  </p>

                  {/* Resend Verification Option */}
                  {(error.toLowerCase().includes('confirm') || error.toLowerCase().includes('verify')) && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleResendVerification}
                      disabled={resendStatus === 'loading'}
                      className="w-full py-3 rounded-xl font-bold text-sm"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff' }}
                    >
                      {resendStatus === 'loading' ? t('auth.signup.creatingAccount') : t('auth.signup.resendVerification', 'Resend Verification Email')}
                    </Button>
                  )}

                  {/* Password Reset Option for credential errors */}
                  {(error.toLowerCase().includes('invalid login credentials') || error.toLowerCase().includes('invalid email or password')) && (
                    <div style={{ marginTop: 8 }}>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fca5a5', textDecoration: 'underline', fontWeight: 700, fontSize: 13, padding: 0 }}
                      >
                        Forgot your password?
                      </button>
                    </div>
                  )}

                  {resendStatus === 'success' && (
                    <p className="text-sm text-emerald-400 mt-2">{t('auth.verification_email_sent_please_check_your')}</p>
                  )}
                </motion.div>
              )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label htmlFor="email" style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
                {t('auth.login.emailLabel')}
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={t('auth.login.emailPlaceholder')}
                className="text-base h-14 px-5 rounded-2xl border-white/10 focus:border-emerald-500 bg-white/06 text-white placeholder:text-white/30"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', borderRadius: 14, height: 52, padding: '0 18px', fontSize: 15 }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label htmlFor="password" style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
                {t('auth.login.passwordLabel')}
              </label>
              <div style={{ position: 'relative' }}>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder={t('auth.login.passwordPlaceholder')}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: '#fff', borderRadius: 14, height: 52, padding: '0 50px 0 18px', fontSize: 15, width: '100%' }}
                />
                <button
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword(v => !v)}
                  style={{ position: 'absolute', inset: 0, left: 'auto', right: 0, width: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)' }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div style={{ textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  style={{ fontSize: 13, color: accent, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {t('auth.login.forgotPassword', 'Forgot your password?')}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full font-black rounded-2xl"
              style={{ height: 52, fontSize: 16, background: `linear-gradient(135deg, ${accent}cc, ${accent})`, color: '#020617', border: 'none', boxShadow: `0 8px 28px ${accent}44` }}
              disabled={isSubmitting || isGoogleLoading}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <LoadingSpinner size="sm" className="mr-3" />
                  {t('auth.login.signingIn')}
                </div>
              ) : (
                t('auth.login.submitButton')
              )}
            </Button>
          </form>

          {/* Social Login Section */}
          <div className="mt-12">
            <SocialLoginSection
              onGoogleSignIn={handleGoogleSignIn}
              onFacebookSignIn={handleFacebookSignIn}
              isGoogleLoading={isGoogleLoading}
              isFacebookLoading={isFacebookLoading}
              disabled={isSubmitting}
            />
          </div>

          {!facilityBranding.is_branded && (
            <div style={{ marginTop: 28, textAlign: 'center' }}>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
                {t('auth.login.noAccount')}{' '}
                <Link to="/auth/signup" style={{ color: accent, fontWeight: 700, textDecoration: 'none' }}>
                  {t('auth.login.signUpLink')}
                </Link>
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 20 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{ maxWidth: 480, width: '100%', background: '#070f24', border: '1px solid rgba(14,165,233,0.18)', borderRadius: 24, padding: '36px 32px', boxShadow: '0 25px 80px rgba(0,0,0,0.8)', position: 'relative' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontSize: 24, fontWeight: 900, color: '#fff', margin: 0, letterSpacing: '-0.02em' }}>
                {t('auth.login.resetPasswordTitle', 'Reset Password')}
              </h3>
              <button
                onClick={() => {
                  setShowForgotPassword(false)
                  setResetMessage('')
                  setForgotPasswordEmail('')
                }}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 4 }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginBottom: 24, lineHeight: 1.5 }}>
              {t('auth.login.resetPasswordDesc', "Enter your email address and we'll send you a link to reset your password.")}
            </p>

            {resetMessage && (
              <div style={{
                marginBottom: 20,
                padding: '14px 18px',
                borderRadius: 14,
                fontSize: 14,
                fontWeight: 600,
                background: resetMessage.includes('Error') ? 'rgba(239, 68, 68, 0.12)' : 'rgba(16, 185, 129, 0.12)',
                border: `1px solid ${resetMessage.includes('Error') ? 'rgba(239, 68, 68, 0.25)' : 'rgba(16, 185, 129, 0.25)'}`,
                color: resetMessage.includes('Error') ? '#fca5a5' : '#6ee7b7'
              }}>
                {resetMessage}
              </div>
            )}

            <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label htmlFor="forgotEmail" style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
                  {t('auth.login.resetEmailLabel', 'Email Address')}
                </label>
                <Input
                  id="forgotEmail"
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  required
                  placeholder={t('auth.login.resetEmailPlaceholder', 'Enter your email address')}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', borderRadius: 14, height: 50, padding: '0 16px', fontSize: 15 }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false)
                    setResetMessage('')
                    setForgotPasswordEmail('')
                  }}
                  style={{ flex: 1, height: 48, borderRadius: 14, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.75)', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                >
                  {t('auth.login.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isResettingPassword}
                  style={{ flex: 1, height: 48, borderRadius: 14, background: `linear-gradient(135deg, ${accent}cc, ${accent})`, border: 'none', color: '#020617', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}
                >
                  {isResettingPassword ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <LoadingSpinner size="sm" />
                      {t('auth.login.sending', 'Sending...')}
                    </div>
                  ) : (
                    t('auth.login.sendLink', 'Send Link')
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  )
}